import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { Call } from 'app/models/dto/call.model';
import { CallHistoryService } from 'app/services/call/call-history.service';
import { ClearBorrowerStore } from 'app/store/borrower/borrower/borrower.actions';
import {tap, take, of} from 'rxjs';
import { DispatchBorrowerContextActions } from '../user-action-items.action';
import { CallSelection, GetUserCallHistory, LoadMore, SortSelection, UserSpecified } from './call-history.action';
import { FilterSelection } from './call-history.action';
import { MissedCallMarkedRead } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.action';
import {catchError} from 'rxjs/operators';

export class UserCallHistoryStateModel {
  lenderUserId: number | null;
  history: Call[];
  selectedCall: Call | null;
  loading: boolean;
  error: any;
  filter: string;
  sort: string;
  missedCallCount: number;
  voicemailsCount: number;
  page: number;
  nextPage: number;
  lastPage: number;
  to: number;
  total: number;
}

@State<UserCallHistoryStateModel>( {
  name: 'UserCallHistory',
  defaults: {
    lenderUserId: null,
    history: [],
    selectedCall: null,
    loading: true,
    error: false,
    filter: 'all',
    sort: 'desc',
    missedCallCount: 0,
    voicemailsCount: 0,
    page: 1,
    nextPage: 2,
    lastPage: 1,
    to: 0,
    total: 0,
  },
} )

@Injectable()
export class UserCallHistoryState {

  static stateByKey(key: string) {
    return createSelector([ UserCallHistoryState], (state: UserCallHistoryStateModel) => state[key]);
  }

  constructor (
    private store: Store,
    private callHistoryService: CallHistoryService,
  ) {}

  @Action( UserSpecified )
  specifyUser (
    { patchState }: StateContext<UserCallHistoryStateModel>,
    { lenderUserId }
  ) {
    patchState({ lenderUserId });
    return this.store.dispatch( new GetUserCallHistory() );
  }

  @Action( GetUserCallHistory )
  getUserCallHistory (
    { getState, patchState }: StateContext<UserCallHistoryStateModel>,
  ) {
    patchState({ loading: true });
    const { history, lenderUserId, filter, sort, page} = getState();
    if (!lenderUserId) {
      return patchState({ error: 'No user specified.' });
    }
    this.callHistoryService.get(lenderUserId, filter, sort, page).pipe(
      tap((response) => {
        patchState({
          lenderUserId,
          history: this.handleHistory(history, response),
          loading: false,
          page: response.current_page,
          lastPage: response.last_page,
          total: response.total,
          selectedCall: response.data[0],
          nextPage: this.getNextPageUrl(response.next_page_url)
        });
      }),
      catchError( error => {
        console.error(error);
        return of();
      }),
    ).subscribe();
  }

  @Action( FilterSelection )
  handleFilterSelection (
    { getState, patchState }: StateContext<UserCallHistoryStateModel>,
    { filter }
  ) {
    const currentFilter = getState().filter;
    if (currentFilter !== filter) {
      patchState({ filter });
      return this.store.dispatch( new GetUserCallHistory() );
    }
  }

  @Action( SortSelection )
  handleSortSelection (
    { getState, patchState }: StateContext<UserCallHistoryStateModel>,
    { sort }
  ) {
    const currentSort = getState().sort;
    if (currentSort !== sort) {
      patchState({ sort });
      return this.store.dispatch( new GetUserCallHistory() );
    }
  }

  @Action( CallSelection )
  handleCallSelection (
    { getState, patchState }: StateContext<UserCallHistoryStateModel>,
    { call }
  ) {
    const previousCall = getState().selectedCall;
    if(previousCall?.id !== call?.id) {
      patchState({ selectedCall: call });
      if (call?.borrowerId && previousCall?.borrowerId !== call.borrowerId || !call?.borrowerId) {
        this.store.dispatch(new ClearBorrowerStore()).pipe(take(1)).subscribe(() => {
          this.store.dispatch(new DispatchBorrowerContextActions(call.borrowerId));
        });
      }
    }
  }

  @Action( LoadMore )
  loadMore (
  { getState, patchState }: StateContext<UserCallHistoryStateModel>,
  ) {
    patchState({ page: getState().nextPage });
    this.store.dispatch( new GetUserCallHistory() );
  }

  getNextPageUrl(url) {
    return url ? url.split('=')[1] : null;
  }

  handleHistory(history, response) {
    if (response.current_page > 1 && response.current_page <= response.last_page) {
      return [...history, ...response.data ];
    }
    return response.data;
  }
}
